import { useEffect } from "react";
import { useState } from "react";
import { useSelector } from "src/redux/store";
import ROLES_ENUM from "src/utils/enums";
import { withLegacyAliases } from "src/utils/employeeActionAliases";
import useAuth from "./useAuth";

// Política del sistema de permisos (2026-08-XX):
//
// El TPV NO oculta acciones operativas por permiso — el supervisor
// override las cubre. Camarero ve TODOS los botones de operativa (eliminar
// línea, cobrar, abrir cajón, cerrar mesa, aplicar descuento…). Al pulsar,
// si no tiene el permiso, salta el diálogo PIN para autorización de un
// supervisor. El flujo es transparente y fluido — nada de "no aparece el
// botón" que dejaba al camarero sin saber qué hacer.
//
// Los flags OPERATIVOS de este hook devuelven true SIEMPRE para no
// re-esconder los botones. Se mantienen exportados para no romper los
// ~30 callers existentes — cuando se elimine todo el legacy podrán
// borrarse. El backend + interceptor axios + supervisor override son la
// autoridad real ahora.
//
// Los flags ADMINISTRATIVOS (accessCaja, allowMakeArqueo) SIGUEN
// chequeando el permiso real. Estos ocultan menús de configuración
// que no tienen sentido mostrar a camareros aunque un supervisor
// pudiera autorizarlos (ruido visual + exposición innecesaria).

const useEmployeeRole = () => {
  const { user } = useAuth();
  const { employees, currentActiveEmployee } = useSelector(
    (state) => state.employees
  );

  const [permissions, setPermissions] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  // Flags ADMINISTRATIVOS: siguen respetando el permiso real.
  const [accessCaja, setAccessCaja] = useState(false);
  const [allowMakeArqueo, setAllowMakeArqueo] = useState(false);

  useEffect(() => {
    if (!user) { resetAdminFlags(true); return; }
    // 2026-08-25 · YA NO exigimos `user.requestUserIdentity` — si el
    // admin ha creado roles y ha asignado uno al empleado, es señal
    // suficiente de que quiere que aplique. El switch legacy
    // "Activar control de empleados" en Ajustes era una capa extra
    // confusa (obligar a re-identificar en cada acción); ahora el
    // control se ejerce sencillamente CON o SIN rol.
    //
    // Regla nueva:
    //   • Empleado activo (currentActiveEmployee > 0) + rol con
    //     `actions[]` no vacío → aplicamos los permisos del rol.
    //   • Sin empleado activo (admin dueño) o rol sin acciones
    //     → pass-through (todo permitido).

    const employeeId = Number(window.localStorage.getItem("UserEmployeeId"));
    if (currentActiveEmployee === 0 || !employeeId || employeeId === 0) {
      resetAdminFlags(true);
      return;
    }
    if (!currentActiveEmployee || !employees || !employees.length) {
      resetAdminFlags(true);
      return;
    }
    const employee = employees.find((e) => e.id === currentActiveEmployee);
    if (!employee || !employee.roleNavegation || !employee.roleNavegation.actions) {
      resetAdminFlags(true);
      return;
    }
    // Rol asignado pero SIN acciones marcadas → pass-through como si
    // no tuviera rol (evita bloquear todo por un rol vacío recién creado).
    if (employee.roleNavegation.actions.length === 0) {
      resetAdminFlags(true);
      return;
    }

    // Expandimos con aliases legacy: las filas de EmployeeRoleActions
    // ahora llevan codenames nuevos del enum EmployeeAction; el resto
    // del front sigue chequeando strings viejos vía ROLES_ENUM.X.
    const rawActions = employee.roleNavegation.actions.map((a) => a.name);
    const actions = withLegacyAliases(rawActions);

    setIsReadOnly(employee.roleNavegation.isReadOnly);
    // Admin (siguen chequeando):
    setAccessCaja(actions.includes(ROLES_ENUM.ACCESS_CAJA));
    setAllowMakeArqueo(actions.includes(ROLES_ENUM.ALLOW_MAKE_ARQUEO));
    // Permissions completos (nuevos + legacy) para NavBar y otros
    // consumidores que hacen `permissions.includes(ROLES_ENUM.X)`
    // para navegación admin.
    setPermissions(actions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActiveEmployee, user, employees]);

  // "Sin restricción": admin dueño operando o TPV sin identificación
  // requerida. Pintamos accessCaja/allowMakeArqueo también como true
  // para no ocultar Caja al dueño (que es su propio TPV).
  const resetAdminFlags = (allTrue) => {
    setIsReadOnly(false);
    setAccessCaja(!!allTrue);
    setAllowMakeArqueo(!!allTrue);
    let perms = Object.values(ROLES_ENUM);
    if (!user?.cajaAvailable) perms = perms.filter((p) => p !== ROLES_ENUM.ACCESS_CAJA);
    if (!user?.tablesAvailable) perms = perms.filter((p) => p !== ROLES_ENUM.ALLOW_CLOSE_TABLE);
    if (!user?.productsAvailable) perms = perms.filter((p) => p !== ROLES_ENUM.INVENTARY_PRODUCT_MANAGER);
    if (!user?.categoryAvailable) perms = perms.filter((p) => p !== ROLES_ENUM.INVENTARY_CATEGORIES_MANAGER);
    if (!user?.productsAvailable && !user?.categoryAvailable) {
      perms = perms.filter((p) => p !== ROLES_ENUM.INVENTARY_MANAGER);
    }
    setPermissions(perms);
  };

  return {
    permissions,
    isReadOnly,
    // Los siguientes 4 flags devuelven SIEMPRE true por diseño (ver
    // política arriba). Se mantienen exportados por compatibilidad
    // con callers legacy. Cuando se ejecuten las acciones, el backend
    // rebota 403 si el empleado no tiene el permiso real y el
    // interceptor axios abre el PIN dialog para override.
    allowDeleteProducts: true,
    allowOpenCashier: true,
    allowCobro: true,
    allowCloseTable: true,
    // Flags administrativos: sí ocultan menús de configuración.
    accessCaja,
    allowMakeArqueo,
  };
};

export default useEmployeeRole;
